import React, { useState, useEffect, useCallback } from "react";
import api from '../../lib/axios';
import { toast } from "sonner";
import { TrendingUp, Calendar } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  ComposedChart
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { API } from "@/utils/constants";
import { formatCurrency, formatNumber } from "@/utils/formatters";

/**
 * Get CSS style classes based on numeric change value
 * @param {number} value - The change value to evaluate
 * @returns {{bg: string, text: string}} Object with background and text color classes
 * @example
 * getChangeStyle(10)  // returns { bg: 'bg-red-50', text: 'text-red-600' }
 * getChangeStyle(-5)  // returns { bg: 'bg-emerald-50', text: 'text-emerald-600' }
 * getChangeStyle(0)   // returns { bg: 'bg-slate-50', text: 'text-slate-600' }
 */
const getChangeStyle = (value) => {
  if (value === 0) return { bg: 'bg-slate-50', text: 'text-slate-600' };
  if (value > 0) return { bg: 'bg-red-50', text: 'text-red-600' };
  return { bg: 'bg-emerald-50', text: 'text-emerald-600' };
};

/**
 * Format date string for display in Spanish locale
 * @param {string} dateStr - ISO date string
 * @returns {string} Formatted date (e.g., "15 ene")
 */
const formatDateDisplay = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
};

// Custom tooltip component (moved outside main component)
const CustomChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="font-medium text-slate-900 mb-2">{formatDateDisplay(label)}</p>
        <div className="space-y-1 text-sm">
          <p className="text-emerald-600">
            <span className="text-slate-500">Promedio:</span> {formatCurrency(data.average_cost_per_m2)}/m²
          </p>
          <p className="text-blue-600">
            <span className="text-slate-500">Mín:</span> {formatCurrency(data.min_cost_per_m2)}/m²
          </p>
          <p className="text-orange-600">
            <span className="text-slate-500">Máx:</span> {formatCurrency(data.max_cost_per_m2)}/m²
          </p>
          <p className="text-slate-600">
            <span className="text-slate-500">Muestras:</span> {data.entries_count}
          </p>
        </div>
      </div>
    );
  }
  return null;
};

// Legend formatter (moved outside)
const legendFormatter = (value) => <span className="text-sm text-slate-600">{value}</span>;

const PriceTrendModal = ({ typeId, typeName, isOpen, onClose }) => {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`${API}/quote-types/${typeId}/price-history?limit=90`);
      setHistory(response.data.history || []);
    } catch (error) {
      toast.error("Error al cargar historial de precios");
    } finally {
      setIsLoading(false);
    }
  }, [typeId]);

  useEffect(() => {
    if (isOpen && typeId) {
      fetchHistory();
    }
  }, [isOpen, typeId, fetchHistory]);

  // Calculate stats
  const calculateStats = () => {
    if (history.length === 0) return null;
    
    const firstPrice = history[0]?.average_cost_per_m2 || 0;
    const lastPrice = history[history.length - 1]?.average_cost_per_m2 || 0;
    const change = lastPrice - firstPrice;
    const changePercent = firstPrice > 0 ? (change / firstPrice) * 100 : 0;
    
    const allPrices = history.map(h => h.average_cost_per_m2);
    const minPrice = Math.min(...allPrices);
    const maxPrice = Math.max(...allPrices);
    
    return { firstPrice, lastPrice, change, changePercent, minPrice, maxPrice };
  };

  const stats = calculateStats();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Tendencia de Precios
          </DialogTitle>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span className="font-medium text-slate-900">{typeName}</span>
            <Badge variant="secondary">
              <Calendar className="w-3 h-3 mr-1" />
              Últimos {history.length} registros
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-10 h-10 border-4 border-slate-200 border-t-emerald-500 rounded-full animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <TrendingUp className="w-12 h-12 mb-4 text-slate-300" />
              <p className="font-medium">Sin historial de precios</p>
              <p className="text-sm mt-1">Agrega más cotizaciones para ver la tendencia</p>
            </div>
          ) : (
            <>
              {/* Stats summary */}
              {stats && (
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-slate-500 uppercase">Precio Inicial</p>
                    <p className="text-lg font-bold text-slate-900">{formatCurrency(stats.firstPrice)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-slate-500 uppercase">Precio Actual</p>
                    <p className="text-lg font-bold text-slate-900">{formatCurrency(stats.lastPrice)}</p>
                  </div>
                  <div className={`rounded-lg p-3 text-center ${getChangeStyle(stats.change).bg}`}>
                    <p className="text-xs text-slate-500 uppercase">Variación</p>
                    <p className={`text-lg font-bold ${getChangeStyle(stats.change).text}`}>
                      {stats.change > 0 ? '+' : ''}{formatCurrency(stats.change)}
                    </p>
                  </div>
                  <div className={`rounded-lg p-3 text-center ${getChangeStyle(stats.changePercent).bg}`}>
                    <p className="text-xs text-slate-500 uppercase">% Cambio</p>
                    <p className={`text-lg font-bold ${getChangeStyle(stats.changePercent).text}`}>
                      {stats.changePercent > 0 ? '+' : ''}{formatNumber(stats.changePercent, 1)}%
                    </p>
                  </div>
                </div>
              )}

              {/* Chart */}
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={history}
                    margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                  >
                    <defs>
                      <linearGradient id="colorRange" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={formatDateDisplay}
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <YAxis 
                      tickFormatter={(v) => `$${(v/1000).toFixed(1)}k`}
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      axisLine={{ stroke: '#e2e8f0' }}
                      domain={['dataMin - 100', 'dataMax + 100']}
                    />
                    <Tooltip content={<CustomChartTooltip />} />
                    <Legend 
                      verticalAlign="top" 
                      height={36}
                      formatter={legendFormatter}
                    />
                    
                    {/* Range area between min and max */}
                    <Area
                      type="monotone"
                      dataKey="max_cost_per_m2"
                      stroke="none"
                      fill="url(#colorRange)"
                      name="Rango"
                    />
                    
                    {/* Lines */}
                    <Line 
                      type="monotone" 
                      dataKey="average_cost_per_m2" 
                      stroke="#10b981" 
                      strokeWidth={3}
                      dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, fill: '#10b981' }}
                      name="Promedio/m²"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="min_cost_per_m2" 
                      stroke="#3b82f6" 
                      strokeWidth={1}
                      strokeDasharray="5 5"
                      dot={false}
                      name="Mínimo/m²"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="max_cost_per_m2" 
                      stroke="#f97316" 
                      strokeWidth={1}
                      strokeDasharray="5 5"
                      dot={false}
                      name="Máximo/m²"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Info note */}
              <p className="text-xs text-slate-500 text-center">
                El historial se actualiza cada vez que se agrega una nueva cotización
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PriceTrendModal;
